import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { storeReferral, trackReferral, logPartnerEvent } from "@/lib/referral";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Iter XX.44.G — Enhanced Public Partner Profile.
 * Routes: `/@:username` (canonical), `/p/:username` (fallback), `/:handle`
 * (catch-all guarded by leading `@`).
 *
 * Additions on top of Iter XX.43:
 *   • Tagline strip.
 *   • Achievements grid.
 *   • Testimonials carousel (static, mobile-first).
 *   • Photo gallery.
 *   • Session pricing block with per-tier CTAs.
 *   • Booking CTA + academy website link + Google Maps embed.
 *   • "Recommended by X" attribution banner when arrived through
 *     a referral (?ref=user OR /r/CODE OR stored referral cookie).
 *
 * The profile NEVER surfaces any private player data. All fields are
 * partner-owned public copy.
 */
export default function PartnerProfile() {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawHandle = params.handle || "";
  const raw = params.username || (rawHandle.startsWith("@") ? rawHandle.slice(1) : "") || "";
  const username = raw.replace(/^@/, "").toLowerCase();
  const [state, setState] = useState({ loading: true, error: null, partner: null, recommendedBy: null });
  const [copied, setCopied] = useState(false);

  // Iter XX.178 — Blank-screen guardrail.
  // The catch-all router entry `<Route path="/:handle" element={<PartnerProfile />} />`
  // in App.js swallows any first-segment URL that isn't explicitly
  // routed (e.g. `/register`, `/teams`, typos). PartnerProfile is
  // designed to only handle `@username`; for anything else, this
  // component previously rendered a full-viewport "Couldn't load this
  // profile" message, which read as a black error screen. Redirect to
  // landing instead so accidental navigation never lands in an error
  // UI. `/p/:username` (fallback route without `@`) still works
  // because it fills `params.username`, so we only redirect when the
  // handle came from `/:handle` AND doesn't start with `@`.
  useEffect(() => {
    if (!params.username && rawHandle && !rawHandle.startsWith("@")) {
      navigate("/", { replace: true });
    }
  }, [rawHandle, params.username, navigate]);

  useEffect(() => {
    let alive = true;
    async function load() {
      if (!username) { setState({ loading: false, error: "invalid", partner: null, recommendedBy: null }); return; }
      // "Recommended by X" ONLY fires when the visitor arrived through
      // an EXPLICIT ?ref= / ?rc= in the URL — never from the stored
      // referral cookie. That cookie is for signup attribution and
      // must not be surfaced as social endorsement on unrelated pages.
      const ref = searchParams.get("ref") || "";
      const refCode = searchParams.get("rc") || "";
      const params2 = new URLSearchParams();
      if (ref && ref.toLowerCase() !== username) params2.set("ref", ref);
      if (refCode) params2.set("ref_code", refCode);
      const qs = params2.toString() ? `?${params2.toString()}` : "";
      try {
        const r = await fetch(`${API}/api/partners/public/${encodeURIComponent(username)}${qs}`);
        if (!r.ok) {
          if (alive) setState({ loading: false, error: "not_found", partner: null, recommendedBy: null });
          return;
        }
        const data = await r.json();
        if (!alive) return;
        setState({ loading: false, error: null, partner: data.partner, recommendedBy: data.recommended_by });
        // First-touch — store referral cookie for later signup on THIS partner.
        storeReferral(username);
        trackReferral({ ref: username });
      } catch {
        if (alive) setState({ loading: false, error: "network", partner: null, recommendedBy: null });
      }
    }
    load();
    return () => { alive = false; };
  }, [username, searchParams]);

  const partner = state.partner;
  const recommendedBy = state.recommendedBy;

  async function handleTryGully() {
    if (partner) await logPartnerEvent(partner.username, "partner_gully_cta_clicked");
    const q = partner ? `?ref=${encodeURIComponent(partner.username)}` : "";
    navigate(`/login${q}`);
  }

  async function handleSocialClick(link) {
    if (partner) {
      await logPartnerEvent(partner.username, "partner_social_link_clicked", { platform: link.platform });
    }
    window.open(link.url, "_blank", "noopener,noreferrer");
  }

  async function handleCTA() {
    if (!partner) return;
    if (partner.primary_cta_type === "gully" || !partner.primary_cta_url) {
      await handleTryGully();
      return;
    }
    await logPartnerEvent(partner.username, "partner_primary_cta_clicked");
    window.open(partner.primary_cta_url, "_blank", "noopener,noreferrer");
  }

  async function handleBooking() {
    if (!partner?.booking_url) return;
    await logPartnerEvent(partner.username, "partner_booking_clicked");
    window.open(partner.booking_url, "_blank", "noopener,noreferrer");
  }

  async function handleWebsite() {
    if (!partner?.website_url) return;
    await logPartnerEvent(partner.username, "partner_website_clicked");
    window.open(partner.website_url, "_blank", "noopener,noreferrer");
  }

  async function handleMapOpen() {
    if (!partner?.map_query) return;
    await logPartnerEvent(partner.username, "partner_map_opened");
    const q = encodeURIComponent(partner.map_query);
    window.open(`https://www.google.com/maps/search/?api=1&query=${q}`, "_blank", "noopener,noreferrer");
  }

  async function handlePricingCta(tier) {
    if (!partner) return;
    await logPartnerEvent(partner.username, "partner_pricing_cta_clicked", { tier: tier.name });
    if (tier.cta_url) {
      window.open(tier.cta_url, "_blank", "noopener,noreferrer");
    } else if (partner.booking_url) {
      window.open(partner.booking_url, "_blank", "noopener,noreferrer");
    } else {
      await handleTryGully();
    }
  }

  async function handleShare() {
    if (!partner) return;
    const url = partner.public_url || window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: partner.display_name, url });
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }
      await logPartnerEvent(partner.username, "partner_profile_shared");
    } catch { /* user dismissed */ }
  }

  if (state.loading) {
    return (
      <div className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center">
        <div className="animate-pulse text-neutral-400">Loading…</div>
      </div>
    );
  }
  if (state.error === "not_found" || state.error === "invalid") {
    return (
      <div className="min-h-screen bg-neutral-950 text-neutral-100 flex flex-col items-center justify-center px-6 text-center">
        <div className="text-3xl mb-2">Profile not found</div>
        <div className="text-neutral-400 mb-6">{"This Gully page doesn't exist or is no longer active."}</div>
        <Link to="/" className="text-emerald-400 hover:text-emerald-300" data-testid="partner-not-found-home">Go to Gully</Link>
      </div>
    );
  }
  if (state.error || !partner) {
    return (
      <div className="min-h-screen bg-neutral-950 text-neutral-100 flex flex-col items-center justify-center px-6 text-center">
        <div className="text-neutral-400 mb-6">{"Couldn't load this profile. Please try again."}</div>
        <Link to="/" className="text-emerald-400 hover:text-emerald-300" data-testid="partner-error-home">Go to Gully</Link>
      </div>
    );
  }

  const location = [partner.city, partner.country].filter(Boolean).join(", ");
  const verified = partner.verification_status === "verified";
  const mapEmbed = partner.map_query
    ? `https://www.google.com/maps?q=${encodeURIComponent(partner.map_query)}&output=embed`
    : null;

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100" data-testid="partner-profile">
      {/* Recommended-by banner */}
      {recommendedBy && recommendedBy.username && (
        <div
          className="w-full bg-emerald-950/60 border-b border-emerald-800/50 py-2.5 px-6 text-center text-sm text-emerald-200"
          data-testid="partner-recommended-by-banner"
        >
          Recommended by{" "}
          <Link
            to={`/@${encodeURIComponent(recommendedBy.username)}`}
            className="font-medium underline decoration-dotted underline-offset-2 hover:text-emerald-100"
            data-testid="partner-recommended-by-link"
            onClick={() => logPartnerEvent(partner.username, "partner_recommended_by_clicked", { platform: recommendedBy.username })}
          >
            {recommendedBy.display_name || recommendedBy.username}
            {recommendedBy.verified && <span className="ml-1" aria-label="verified">✓</span>}
          </Link>
        </div>
      )}

      <div className="max-w-2xl mx-auto px-6 py-10">
        {/* Header: avatar + name + verified badge */}
        <div className="flex flex-col items-center text-center mb-6">
          <div className="w-28 h-28 rounded-full bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 border border-emerald-500/20 flex items-center justify-center overflow-hidden mb-4">
            {partner.profile_image_url ? (
              <img
                src={partner.profile_image_url}
                alt={partner.display_name}
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <div className="text-4xl font-serif text-emerald-400">
                {(partner.display_name || "G").charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold" data-testid="partner-display-name">
              {partner.display_name}
            </h1>
            {verified && (
              <span
                className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500 text-neutral-950 text-xs font-bold"
                title="Verified partner"
                data-testid="partner-verified-badge"
              >✓</span>
            )}
          </div>
          <div className="text-sm text-neutral-400 mt-1" data-testid="partner-username">@{partner.username}</div>
          <div className="text-xs text-neutral-500 mt-1">
            {partner.partner_type ? partner.partner_type.charAt(0).toUpperCase() + partner.partner_type.slice(1) : ""}
            {location ? ` · ${location}` : ""}
          </div>
          {partner.tagline && (
            <div className="text-emerald-300 text-sm mt-3 font-medium" data-testid="partner-tagline">
              {partner.tagline}
            </div>
          )}
          {partner.bio && (
            <p className="text-neutral-300 text-sm mt-4 leading-relaxed" data-testid="partner-bio">
              {partner.bio}
            </p>
          )}
        </div>

        {/* Primary CTA */}
        <Button
          onClick={handleCTA}
          className="w-full mb-3 bg-emerald-500 hover:bg-emerald-400 text-neutral-950 font-medium"
          data-testid="partner-primary-cta"
        >
          {partner.primary_cta_label || (partner.primary_cta_type === "gully" ? "Try Gully" : "Learn more")}
        </Button>

        {partner.primary_cta_type !== "gully" && (
          <Button
            variant="outline"
            onClick={handleTryGully}
            className="w-full mb-3 border-neutral-700 hover:bg-neutral-900"
            data-testid="partner-try-gully-btn"
          >
            Try Gully through {partner.display_name}
          </Button>
        )}

        {/* Booking + Website row */}
        {(partner.booking_url || partner.website_url) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4" data-testid="partner-links-row">
            {partner.booking_url && (
              <Button
                variant="outline"
                onClick={handleBooking}
                className="border-emerald-700/60 hover:bg-emerald-950/40 text-emerald-200"
                data-testid="partner-booking-btn"
              >
                Book a session →
              </Button>
            )}
            {partner.website_url && (
              <Button
                variant="outline"
                onClick={handleWebsite}
                className="border-neutral-700 hover:bg-neutral-900"
                data-testid="partner-website-btn"
              >
                Visit website →
              </Button>
            )}
          </div>
        )}

        {/* Achievements */}
        {partner.achievements && partner.achievements.length > 0 && (
          <section className="mt-10" data-testid="partner-achievements">
            <h2 className="text-xs uppercase tracking-wider text-neutral-500 mb-3">Achievements</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {partner.achievements.map((a, i) => (
                <div key={i} className="bg-neutral-900 border border-neutral-800 rounded-lg p-3" data-testid={`partner-achievement-${i}`}>
                  <div className="text-sm font-medium text-neutral-100">{a.title}</div>
                  {a.subtitle && <div className="text-xs text-neutral-400 mt-0.5">{a.subtitle}</div>}
                  {a.year && <div className="text-xs text-emerald-400/70 mt-0.5">{a.year}</div>}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Testimonials */}
        {partner.testimonials && partner.testimonials.length > 0 && (
          <section className="mt-10" data-testid="partner-testimonials">
            <h2 className="text-xs uppercase tracking-wider text-neutral-500 mb-3">What people say</h2>
            <div className="space-y-3">
              {partner.testimonials.map((t, i) => (
                <blockquote
                  key={i}
                  className="bg-neutral-900 border-l-2 border-emerald-500/60 pl-4 pr-3 py-3 rounded-r-lg"
                  data-testid={`partner-testimonial-${i}`}
                >
                  <p className="text-sm text-neutral-200 italic">&ldquo;{t.quote}&rdquo;</p>
                  {(t.attribution || t.role) && (
                    <footer className="text-xs text-neutral-500 mt-2">
                      {t.attribution || ""}{t.role ? ` · ${t.role}` : ""}
                    </footer>
                  )}
                </blockquote>
              ))}
            </div>
          </section>
        )}

        {/* Gallery */}
        {partner.gallery && partner.gallery.length > 0 && (
          <section className="mt-10" data-testid="partner-gallery">
            <h2 className="text-xs uppercase tracking-wider text-neutral-500 mb-3">Gallery</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {partner.gallery.map((g, i) => (
                <a
                  key={i}
                  href={g.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="aspect-square bg-neutral-900 rounded-lg overflow-hidden border border-neutral-800 hover:border-emerald-500/40 transition"
                  data-testid={`partner-gallery-item-${i}`}
                  onClick={() => logPartnerEvent(partner.username, "partner_gallery_viewed", { platform: String(i) })}
                >
                  <img
                    src={g.url}
                    alt={g.caption || `Gallery item ${i + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                </a>
              ))}
            </div>
          </section>
        )}

        {/* Pricing */}
        {partner.pricing_tiers && partner.pricing_tiers.length > 0 && (
          <section className="mt-10" data-testid="partner-pricing">
            <h2 className="text-xs uppercase tracking-wider text-neutral-500 mb-3">Sessions</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {partner.pricing_tiers.map((t, i) => (
                <div key={i} className="bg-neutral-900 border border-neutral-800 rounded-lg p-4 flex flex-col" data-testid={`partner-pricing-tier-${i}`}>
                  <div className="flex items-baseline justify-between mb-1">
                    <div className="text-sm font-medium text-neutral-100">{t.name}</div>
                    {t.price_display && (
                      <div className="text-emerald-300 text-sm font-semibold">{t.price_display}</div>
                    )}
                  </div>
                  {t.description && <div className="text-xs text-neutral-400 mb-3 flex-1">{t.description}</div>}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePricingCta(t)}
                    className="border-neutral-700 hover:bg-neutral-800 mt-auto"
                    data-testid={`partner-pricing-cta-${i}`}
                  >
                    {t.cta_label || "Enquire"} →
                  </Button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Map */}
        {mapEmbed && (
          <section className="mt-10" data-testid="partner-map">
            <h2 className="text-xs uppercase tracking-wider text-neutral-500 mb-3">Where to find us</h2>
            <div className="rounded-lg overflow-hidden border border-neutral-800 aspect-video bg-neutral-900">
              <iframe
                title={`Map for ${partner.display_name}`}
                src={mapEmbed}
                className="w-full h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="partner-map-iframe"
              />
            </div>
            <button
              onClick={handleMapOpen}
              className="text-xs text-emerald-400 hover:text-emerald-300 mt-2"
              data-testid="partner-map-open-btn"
            >
              Open in Google Maps →
            </button>
          </section>
        )}

        {/* Social links */}
        {partner.social_links && partner.social_links.length > 0 && (
          <section className="mt-10 space-y-2" data-testid="partner-social-links">
            <h2 className="text-xs uppercase tracking-wider text-neutral-500 mb-3">Follow along</h2>
            {partner.social_links.map((link, idx) => (
              <button
                key={idx}
                onClick={() => handleSocialClick(link)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg bg-neutral-900 hover:bg-neutral-800 border border-neutral-800 text-left transition"
                data-testid={`partner-social-link-${link.platform}`}
              >
                <div>
                  <div className="text-sm font-medium capitalize">
                    {link.platform === "x" ? "X (Twitter)" : link.platform.replace(/_/g, " ")}
                  </div>
                  {link.label && <div className="text-xs text-neutral-500">{link.label}</div>}
                </div>
                <div className="text-neutral-500">→</div>
              </button>
            ))}
          </section>
        )}

        {/* Share row */}
        <div className="flex gap-2 mt-10">
          <Button
            onClick={handleShare}
            variant="outline"
            className="flex-1 border-neutral-700 hover:bg-neutral-900"
            data-testid="partner-share-btn"
          >
            {copied ? "Copied!" : "Share this page"}
          </Button>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center text-xs text-neutral-600">
          <Link to="/" className="hover:text-neutral-400" data-testid="partner-footer-home">Gully</Link>
          <span className="mx-2">·</span>
          <Link to="/privacy" className="hover:text-neutral-400">Privacy</Link>
          <span className="mx-2">·</span>
          <Link to="/terms" className="hover:text-neutral-400">Terms</Link>
        </div>
      </div>
    </div>
  );
}
